import { useState, useEffect } from 'react'
import { endOfDay } from 'date-fns'
import DatePicker from 'react-datepicker'
import Select from 'react-select'
import * as Yup from 'yup'
import { Formik, Form } from 'formik'
import { InfoIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { useIsSaltoSpaceBuildingConnected } from '@/api/property-keyless/salto-space/use-is-salto-space-building-connected'
import { useGetSaltoSpaceFobs } from '@/api/keyless/salto-space/get-salto-space-fobs'
import ToggleField from '@/components/FormLegacy/FormFieldsV2/ToggleField'
import { Alert } from '@/components/ui/alert'
import { Tooltip } from '@/components/ui/tooltip'

import API from '../../../../api/api'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import {
  saltoSpaceDropdownStyles,
  getSelectedItemsValue,
  getConfirmationText,
  getDisconnectConfirmationText,
} from '../helpers'
import Loader from '../../../../components/Loader/Loader'
import SaltoSpaceExternalFobs, { ExternalFobRecord } from './SaltoSpaceExternalFobs'
import SaltoSpaceIssueFobModal from './SaltoSpaceIssueFobModal'

const getCurrentDate = () => new Date()
const isDateValid = (endDate) => endDate && new Date(endDate) > getCurrentDate()

const SaltoSpaceSection = ({
  isAvailable,
  isFobsAvailable,
  buildingId,
  clientData,
  onClientDataChange,
}) => {
  const { t } = useTranslation(['clients-legacy', 'clients', 'common'])
  const { displaySnackbar } = useSnackbar()
  const { isBuildingConnected, isLoading: isLoadingBuildingConnection } =
    useIsSaltoSpaceBuildingConnected(buildingId)

  const expirationDateInitialValue = isDateValid(clientData.leaseEnd)
    ? clientData.leaseEnd
    : isDateValid(clientData.moveOutDate)
      ? clientData.moveOutDate
      : undefined

  const [isConnectUserPopupVisible, setIsConnectUserPopupVisible] = useState(false)
  const [isCreateNewUserPopupVisible, setIsCreateNewUserPopupVisible] = useState(false)
  const [uniqueSaltoUsers, setUniqueSaltoUsers] = useState([])
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isCreatingNewUser, setIsCreatingNewUser] = useState(false)
  const [isUserConnectedToSaltoSpace, setIsUserConnectedToSaltoSpace] = useState(false)
  const [isSaltoSpaceLoading, setIsSaltoSpaceLoading] = useState(false)
  const [saltoSpaceDoors, setSaltoSpaceDoors] = useState([])
  const [saltoSpaceGroups, setSaltoSpaceGroups] = useState([])
  const [saltoSpaceAccessLevels, setSaltoSpaceAccessLevels] = useState([])
  const [isSubmittingSaltoSpace, setIsSubmittingSaltoSpace] = useState(false)
  const [isUpdatingKeylessAccessSuspended, setIsUpdatingKeylessAccessSuspended] = useState(false)
  const [isKeylessAccessPopupVisible, setIsKeylessAccessPopupVisible] = useState(false)
  const [pendingKeylessAccessSuspendedValue, setPendingKeylessAccessSuspendedValue] = useState(null)
  const [isDisconnectPopupVisible, setIsDisconnectPopupVisible] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [saltoSpaceInitialValues, setSaltoSpaceInitialValues] = useState({
    doors: [],
    groups: [],
    accessLevels: [],
    authorizationCodes: '',
    expirationDate: expirationDateInitialValue,
  })
  const [isFobModalOpen, setIsFobModalOpen] = useState(false)

  const {
    user: { email, firstName, lastName },
  } = clientData
  const residentName = [firstName, lastName].filter(Boolean).join(' ') || email
  const { data: fobs = [], refetch: refetchFobs } = useGetSaltoSpaceFobs(
    buildingId,
    email,
    isFobsAvailable && isUserConnectedToSaltoSpace,
  )
  const internalFob = fobs.find((fob) => fob.fobMarker === 'internal' && fob.fobId) ?? null

  const [fobSelectedDoors, setFobSelectedDoors] = useState([])
  const [fobSelectedGroups, setFobSelectedGroups] = useState([])
  const [fobSelectedAccessLevels, setFobSelectedAccessLevels] = useState([])
  const [fobExpirationDate, setFobExpirationDate] = useState(expirationDateInitialValue)

  const isFobButtonEnabled =
    (fobSelectedDoors.length > 0 ||
      fobSelectedGroups.length > 0 ||
      fobSelectedAccessLevels.length > 0) &&
    !!fobExpirationDate

  const handleIssueFob = (encoderId, force) =>
    API.issueSaltoSpaceFob(
      buildingId,
      encoderId,
      email,
      {
        accessLevelIds: fobSelectedAccessLevels,
        doorIds: fobSelectedDoors,
        expirationDate: fobExpirationDate
          ? endOfDay(new Date(fobExpirationDate)).toISOString()
          : undefined,
        permissionGroupIds: fobSelectedGroups,
      },
      force,
    ).then(() => undefined)

  const SaltoSpaceSchema = Yup.object().shape({
    selectedDoors: Yup.array().of(Yup.string()),
    selectedGroups: Yup.array().of(Yup.string()),
    selectedAccessLevels: Yup.array().of(Yup.string()),
    authorizationCodes: Yup.string(),
    expirationDate: Yup.number(),
  })

  const parseAuthorizationCodes = (value) =>
    (value || '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean)

  const initialValues = {
    saltoSpaceDoors: saltoSpaceDoors || [],
    saltoSpaceGroups: saltoSpaceGroups || [],
    saltoSpaceAccessLevels: saltoSpaceAccessLevels || [],
    expirationDate: saltoSpaceInitialValues.expirationDate,
    selectedDoors: saltoSpaceInitialValues.doors,
    selectedGroups: saltoSpaceInitialValues.groups,
    selectedAccessLevels: saltoSpaceInitialValues.accessLevels,
    authorizationCodes: saltoSpaceInitialValues.authorizationCodes || '',
    selectedGroupsNames: [],
    selectedAccessLevelsNames: [],
    confirmed: false,
  }

  const isSuspending = pendingKeylessAccessSuspendedValue ?? false

  const keylessAccessPopupBody = t(
    isSuspending
      ? 'clients:keylessAccess.saltoSpace.suspendConfirmation.suspend.body'
      : 'clients:keylessAccess.saltoSpace.suspendConfirmation.unsuspend.body',
    { clientName: residentName },
  )
  const keylessAccessConfirmText = t(
    isSuspending
      ? 'clients:keylessAccess.saltoSpace.suspendConfirmation.suspend.confirmText'
      : 'clients:keylessAccess.saltoSpace.suspendConfirmation.unsuspend.confirmText',
  )

  const handleCreateNewUser = async () => {
    try {
      setIsCreatingNewUser(true)
      await API.createSaltoSpaceUser(email, buildingId)
      setIsUserConnectedToSaltoSpace(true)
      displaySnackbar(t('clients:keylessAccess.saltoSpace.toasts.createSuccess'))
    } catch {
      displaySnackbar(t('clients-legacy:profileView.integrations.saltoSpace.createError'), true)
    } finally {
      setIsCreatingNewUser(false)
      setIsCreateNewUserPopupVisible(false)
    }
  }

  const handleConnectToSaltoSpace = async () => {
    try {
      setIsConnecting(true)
      await API.connectToSaltoSpace(email, buildingId, selectedUserId)
      setIsUserConnectedToSaltoSpace(true)
    } catch (error) {
      if (!selectedUserId && error.response.data && error.response.data.errCode === 422) {
        await fetchDisconnectedSaltoSpaceUsers()
      } else {
        displaySnackbar(t('clients-legacy:profileView.integrations.saltoSpaceConnectError'), true)
      }
    } finally {
      setIsConnecting(false)
    }
  }

  const fetchDisconnectedSaltoSpaceUsers = async () => {
    try {
      const { data = [] } = await API.getDisconnectedSaltoSpaceUsers(buildingId)
      setUniqueSaltoUsers(data)
    } catch (err) {
      console.error(err)
    }
  }

  const handleSaltoSpaceSubmit = async (
    selectedDoors,
    selectedGroups,
    selectedAccessLevels,
    expirationDate,
    authorizationCodes,
    confirmed,
  ) => {
    if (!confirmed) return
    setIsSubmittingSaltoSpace(true)
    try {
      setIsConnectUserPopupVisible(false)
      const parsedAuthorizationCodes = parseAuthorizationCodes(authorizationCodes)
      const savedAuthorizationCodes = parseAuthorizationCodes(
        saltoSpaceInitialValues.authorizationCodes,
      )
      await API.updateSaltoSpaceUser(
        selectedDoors,
        selectedGroups,
        selectedAccessLevels,
        expirationDate,
        email,
        parsedAuthorizationCodes.join(',') !== savedAuthorizationCodes.join(',')
          ? parsedAuthorizationCodes
          : undefined,
      )
      displaySnackbar(t('clients-legacy:profileView.integrations.accessGranted'))

      setSaltoSpaceInitialValues({
        doors: selectedDoors,
        groups: selectedGroups,
        accessLevels: selectedAccessLevels,
        authorizationCodes: parsedAuthorizationCodes.join(', '),
        expirationDate,
      })
    } catch {
      displaySnackbar(t('clients-legacy:profileView.integrations.grantAccessError'), true)
    } finally {
      setIsSubmittingSaltoSpace(false)
    }
  }

  const fetchSaltoSpaceAccessData = async () => {
    try {
      const { data } = await API.getBuildingSaltoSpaceAccessPoints(buildingId)
      setSaltoSpaceDoors(data.doors)
      setSaltoSpaceGroups(data.permissionGroups)
      setSaltoSpaceAccessLevels(data.accessLevels)
    } catch {
      displaySnackbar(t('clients-legacy:profileView.integrations.somethingWentWrong'), true)
    }
  }

  const getSaltoSpaceInitialValues = async () => {
    try {
      const { data } = await API.getSaltoSpaceUser(email)
      setSaltoSpaceInitialValues({
        doors: data.doorIds,
        groups: data.permissionGroupIds,
        accessLevels: data.accessLevelIds,
        authorizationCodes: (data.authorizationCodes ?? []).join(', '),
        expirationDate: data.userExpiration
          ? new Date(data.userExpiration).getTime()
          : expirationDateInitialValue
            ? new Date(expirationDateInitialValue).getTime()
            : undefined,
      })
    } catch (error) {
      displaySnackbar(
        error.message || t('clients-legacy:profileView.integrations.somethingWentWrong'),
        true,
      )
    }
  }

  const handleFobSuccess = () => {
    void refetchFobs()
    setFobSelectedDoors([])
    setFobSelectedGroups([])
    setFobSelectedAccessLevels([])
    setFobExpirationDate(expirationDateInitialValue)
  }

  const checkIfUserIsConnectedToSaltoSpace = async () => {
    try {
      setIsSaltoSpaceLoading(true)
      const { data } = await API.isSaltoSpaceUserConnected(email)
      setIsUserConnectedToSaltoSpace(data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSaltoSpaceLoading(false)
    }
  }

  const handleToggleKeylessAccessSuspended = (event) => {
    setPendingKeylessAccessSuspendedValue(event.target.checked)
    setIsKeylessAccessPopupVisible(true)
  }

  const closeKeylessAccessPopup = () => {
    setPendingKeylessAccessSuspendedValue(null)
    setIsKeylessAccessPopupVisible(false)
  }

  const confirmToggleKeylessAccessSuspended = async () => {
    if (typeof pendingKeylessAccessSuspendedValue !== 'boolean') {
      closeKeylessAccessPopup()
      return
    }

    const isSuspending = pendingKeylessAccessSuspendedValue

    try {
      setIsUpdatingKeylessAccessSuspended(true)
      const updatedClient = await API.updateUserData(
        { isKeylessAccessSuspended: isSuspending },
        clientData.id,
        'client',
      )

      onClientDataChange((prev) => ({
        ...prev,
        isKeylessAccessSuspended: updatedClient?.isKeylessAccessSuspended ?? isSuspending,
      }))

      displaySnackbar(
        t(
          isSuspending
            ? 'clients:keylessAccess.saltoSpace.toasts.suspendSuccess'
            : 'clients:keylessAccess.saltoSpace.toasts.unsuspendSuccess',
        ),
      )
    } catch {
      displaySnackbar(t('clients-legacy:profileView.integrations.saltoSpace.suspendError'), true)
    } finally {
      setIsUpdatingKeylessAccessSuspended(false)
      closeKeylessAccessPopup()
    }
  }

  const handleDisconnectProfile = async () => {
    try {
      setIsDisconnecting(true)
      await API.disconnectSaltoSpaceUser(email)
      setIsUserConnectedToSaltoSpace(false)
      setSaltoSpaceInitialValues({
        doors: [],
        groups: [],
        accessLevels: [],
        expirationDate: expirationDateInitialValue,
      })
      displaySnackbar(`${residentName} has been successfully disconnected from Salto Space`)
    } catch {
      displaySnackbar('Error occurred disconnecting from Salto Space. Please try again', true)
    } finally {
      setIsDisconnecting(false)
      setIsDisconnectPopupVisible(false)
    }
  }

  useEffect(() => {
    if (isUserConnectedToSaltoSpace) {
      getSaltoSpaceInitialValues()
    }
  }, [isUserConnectedToSaltoSpace])

  useEffect(() => {
    if (isAvailable) {
      checkIfUserIsConnectedToSaltoSpace()
      fetchSaltoSpaceAccessData()
    }
  }, [isAvailable])

  if (!isAvailable) return null

  if (isLoadingBuildingConnection) {
    return (
      <div className="information-element information-element-column">
        <Loader />
      </div>
    )
  }

  if (!isBuildingConnected) {
    return (
      <div className="information-element information-element-column">
        <Alert
          type="error"
          title={t('clients:keylessAccess.saltoSpace.buildingNotConnected.title')}
          message={t('clients:keylessAccess.saltoSpace.buildingNotConnected.message')}
        />
      </div>
    )
  }

  return (
    <div className="information-element information-element-column">
      {isUserConnectedToSaltoSpace ? (
        <>
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={SaltoSpaceSchema}
            onSubmit={({
              selectedDoors,
              selectedGroups,
              selectedAccessLevels,
              expirationDate,
              authorizationCodes,
              confirmed,
            }) =>
              handleSaltoSpaceSubmit(
                selectedDoors,
                selectedGroups,
                selectedAccessLevels,
                expirationDate,
                authorizationCodes,
                confirmed,
              )
            }
          >
            {({ values, errors, touched, setFieldValue, handleSubmit, resetForm }) => {
              const hasError = (field) => errors[field] && touched[field]
              const { selectedDoors, selectedGroups, selectedAccessLevels } = values

              const doorValue = getSelectedItemsValue(selectedDoors, saltoSpaceDoors)
              const groupValue = getSelectedItemsValue(selectedGroups, saltoSpaceGroups)
              const accessLevelValue = getSelectedItemsValue(
                selectedAccessLevels,
                saltoSpaceAccessLevels,
              )
              const confirmationText = getConfirmationText(t, clientData, values)

              const onChangeDoor = (selectedOptions) => {
                const saltoDoorIds = selectedOptions
                  ? selectedOptions.map((option) => option.saltoId)
                  : []
                setFieldValue('selectedDoors', saltoDoorIds)
              }

              const onChangeGroup = (selectedOptions) => {
                const { ids, names } = selectedOptions.reduce(
                  (acc, option) => {
                    acc.ids.push(option.saltoId)
                    acc.names.push(option.name)
                    return acc
                  },
                  { ids: [], names: [] },
                )

                setFieldValue('selectedGroups', ids)
                setFieldValue('selectedGroupsNames', names)
              }

              const onChangeAccessLevel = (selectedOptions) => {
                const { ids, names } = selectedOptions.reduce(
                  (acc, option) => {
                    acc.ids.push(option.saltoId)
                    acc.names.push(option.name)
                    return acc
                  },
                  { ids: [], names: [] },
                )

                setFieldValue('selectedAccessLevels', ids)
                setFieldValue('selectedAccessLevelsNames', names)
              }

              const onChangeExpirationDate = (selectedDate) => {
                const date = new Date(selectedDate)
                return setFieldValue('expirationDate', date.getTime() || '')
              }

              const handleConfirm = () => {
                setFieldValue('confirmed', true)
                setIsConnectUserPopupVisible(true)
              }

              return (
                <Form>
                  <div className="columns">
                    <div className="description-column">
                      <div className="element-name element-margin-top">
                        {t('clients-legacy:profileView.integrations.saltoSpace.label')}
                      </div>
                      <div className="dropdown-buttons-container">
                        <div className="dropdown-buttons-container-row">
                          <div className="dropdown-container">
                            <div className="dropdown-header">
                              {t('clients-legacy:profileView.integrations.doorLabel')}
                            </div>
                            <Select
                              styles={saltoSpaceDropdownStyles(hasError('selectedDoors'))}
                              classNamePrefix="dropdown-button"
                              options={values.saltoSpaceDoors}
                              getOptionLabel={(option) => option.name.toString()}
                              getOptionValue={(option) => option.saltoId}
                              value={doorValue}
                              onChange={(selectedOptions) => onChangeDoor(selectedOptions)}
                              placeholder={t(
                                'clients-legacy:profileView.integrations.selectDoorPlaceholder',
                              )}
                              isClearable
                              isMulti
                              closeMenuOnSelect={false}
                            />
                            {hasError('selectedDoors') && (
                              <div className="error-validation">{errors.selectedDoors}</div>
                            )}
                          </div>
                          <div className="dropdown-container">
                            <div className="dropdown-header">
                              {t('clients-legacy:profileView.integrations.zoneLabel')}
                            </div>
                            <Select
                              styles={saltoSpaceDropdownStyles(hasError('selectedGroups'))}
                              className="basic-multi-select"
                              classNamePrefix="select"
                              options={values.saltoSpaceGroups}
                              getOptionLabel={(option) => option.name.toString()}
                              getOptionValue={(option) => option.saltoId}
                              value={groupValue}
                              onChange={(selectedOptions) => onChangeGroup(selectedOptions)}
                              placeholder={t('clients:keylessAccess.saltoSpace.zone.placeholder')}
                              isClearable
                              isMulti
                              closeMenuOnSelect={false}
                            />
                            {hasError('selectedGroups') && (
                              <div className="error-validation">{errors.selectedGroups}</div>
                            )}
                          </div>
                        </div>
                        <div className="dropdown-buttons-container-row">
                          <div className="dropdown-container">
                            <div className="dropdown-header">
                              {t('clients-legacy:profileView.integrations.accessLevelLabel')}
                            </div>
                            <Select
                              styles={saltoSpaceDropdownStyles(hasError('selectedAccessLevels'))}
                              className="basic-multi-select"
                              classNamePrefix="select"
                              options={values.saltoSpaceAccessLevels}
                              getOptionLabel={(option) => option.name.toString()}
                              getOptionValue={(option) => option.saltoId}
                              value={accessLevelValue}
                              onChange={(selectedOptions) => onChangeAccessLevel(selectedOptions)}
                              placeholder={t(
                                'clients:keylessAccess.saltoSpace.accessLevel.placeholder',
                              )}
                              isClearable
                              isMulti
                              closeMenuOnSelect={false}
                            />
                            {hasError('selectedAccessLevels') && (
                              <div className="error-validation">{errors.selectedAccessLevels}</div>
                            )}
                          </div>
                          <div className="dropdown-container">
                            <div className="dropdown-header">
                              {t('clients-legacy:profileView.integrations.expirationDateLabel')}
                            </div>
                            <DatePicker
                              className={`dropdown-button ${
                                hasError('expirationDate') && 'dropdown-button-error-color'
                              }`}
                              placeholderText={t(
                                'clients-legacy:profileView.integrations.dateFormatPlaceholder',
                              )}
                              selected={values.expirationDate || null}
                              onChange={(selectedDate) => onChangeExpirationDate(selectedDate)}
                              minDate={getCurrentDate()}
                              isClearable
                            />
                            {hasError('expirationDate') && (
                              <div className="error-validation">{errors.expirationDate}</div>
                            )}
                          </div>
                        </div>
                        <div className="dropdown-buttons-container-row">
                          <div className="dropdown-container">
                            <div className="dropdown-header">
                              {t('clients:keylessAccess.saltoSpace.authorizationCodes.label')}
                            </div>
                            <input
                              type="text"
                              className="dropdown-button"
                              maxLength={255}
                              value={values.authorizationCodes}
                              placeholder={t(
                                'clients:keylessAccess.saltoSpace.authorizationCodes.placeholder',
                              )}
                              onChange={(event) =>
                                setFieldValue('authorizationCodes', event.target.value)
                              }
                            />
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className={`confirm-button button-primary button ${
                          isSubmittingSaltoSpace ? 'is-loading' : ''
                        }`}
                        disabled={isSubmittingSaltoSpace}
                        onClick={() => handleConfirm()}
                      >
                        {t('common:actions.confirm')}
                      </button>
                      <button
                        type="button"
                        className={`disconnect-profile-button button-red button ${
                          isDisconnecting ? 'is-loading' : ''
                        }`}
                        disabled={isDisconnecting}
                        onClick={() => setIsDisconnectPopupVisible(true)}
                      >
                        {t('clients-legacy:profileView.integrations.saltoSpace.disconnectProfile')}
                      </button>
                    </div>
                  </div>
                  <ConfirmPopup
                    title={t('common:confirmPopup.caution')}
                    body={confirmationText}
                    cancelText={t('common:actions.close')}
                    confirmText={t('clients-legacy:profileView.integrations.yesConfirm')}
                    isOpen={isConnectUserPopupVisible}
                    cancelCallback={() => {
                      resetForm()
                      setIsConnectUserPopupVisible(false)
                    }}
                    confirmCallback={handleSubmit}
                    className={'is-caution'}
                  />
                </Form>
              )
            }}
          </Formik>
          <div className="information-element-toggle toggle-container">
            <ToggleField
              label={t('clients-legacy:profileView.integrations.suspendKeylessAccessLabel')}
              name="isKeylessAccessSuspended"
              onChange={handleToggleKeylessAccessSuspended}
              disabled={isUpdatingKeylessAccessSuspended}
              value={clientData.isKeylessAccessSuspended || false}
            />
            <Tooltip triggerIcon={InfoIcon}>
              <div>{t('clients-legacy:profileView.integrations.saltoSpace.tooltip')}</div>
            </Tooltip>
          </div>
          <ConfirmPopup
            title={t('common:confirmPopup.confirmation')}
            body={keylessAccessPopupBody}
            cancelText={t('common:actions.cancel')}
            confirmText={keylessAccessConfirmText}
            isOpen={isKeylessAccessPopupVisible}
            cancelCallback={closeKeylessAccessPopup}
            confirmCallback={confirmToggleKeylessAccessSuspended}
            isLoading={isUpdatingKeylessAccessSuspended}
          />
          <ConfirmPopup
            title="Caution"
            body={getDisconnectConfirmationText(clientData)}
            cancelText="Close"
            confirmText="Yes, confirm"
            isOpen={isDisconnectPopupVisible}
            cancelCallback={() => setIsDisconnectPopupVisible(false)}
            confirmCallback={handleDisconnectProfile}
            className="is-caution"
            isLoading={isDisconnecting}
          />
        </>
      ) : isSaltoSpaceLoading ? (
        <div className="information-element-row">
          <Loader />
        </div>
      ) : (
        <div className="information-element-row">
          <div className="element-name">
            {t('clients-legacy:profileView.integrations.saltoSpace.label')}
          </div>
          {uniqueSaltoUsers.length > 0 ? (
            <div className="columns">
              <div className="column column-right">
                <Select
                  styles={{
                    ...saltoSpaceDropdownStyles(),
                    control: (styles) => ({
                      ...styles,
                      minWidth: '250px',
                    }),
                  }}
                  options={uniqueSaltoUsers.map((user) => ({
                    label: user.name,
                    value: user.saltoUserId,
                  }))}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  value={uniqueSaltoUsers.find((user) => user.value === selectedUserId)}
                  onChange={(selectedOption) =>
                    selectedOption
                      ? setSelectedUserId(selectedOption.value)
                      : setSelectedUserId(null)
                  }
                  placeholder={t('clients-legacy:profileView.integrations.selectUserPlaceholder')}
                  isClearable
                  closeMenuOnSelect
                />
                <button
                  className={`button button-primary ${isConnecting ? 'is-loading' : ''}`}
                  onClick={handleConnectToSaltoSpace}
                  disabled={isConnecting || !selectedUserId}
                >
                  {isConnecting
                    ? t('clients-legacy:profileView.integrations.connectingEllipsis')
                    : t('clients:keylessAccess.saltoSpace.connectToSaltoSpace')}
                </button>
              </div>
            </div>
          ) : (
            <div className="columns">
              <div className="column column-right">
                <button
                  className={`button button-primary ${isCreatingNewUser ? 'is-loading' : ''}`}
                  onClick={() => setIsCreateNewUserPopupVisible(true)}
                  disabled={isConnecting || isCreatingNewUser}
                >
                  {isCreatingNewUser
                    ? t('clients-legacy:profileView.integrations.creatingEllipsis')
                    : t('clients-legacy:profileView.integrations.createNewUser')}
                </button>
                <span>{t('clients:keylessAccess.notConnected.or')}</span>
                <button
                  className={`button button-primary ${isConnecting ? 'is-loading' : ''}`}
                  onClick={handleConnectToSaltoSpace}
                  disabled={isConnecting || isCreatingNewUser}
                >
                  {isConnecting
                    ? t('clients-legacy:profileView.integrations.connectingEllipsis')
                    : t('clients-legacy:profileView.integrations.connectExistingAccount')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {isFobsAvailable && (
        <>
          <div className="columns">
            <div className="description-column">
              <div className="element-name element-margin-top salto-space-fob-title">
                {t('clients-legacy:profileView.integrations.saltoSpace.fobTitle')}
                <Tooltip triggerIcon={InfoIcon}>
                  {t('clients-legacy:profileView.integrations.saltoSpace.fobTooltip')}
                </Tooltip>
              </div>
            </div>
          </div>
          {internalFob ? (
            <ExternalFobRecord
              fob={internalFob}
              buildingId={buildingId}
              email={email}
              saltoSpaceDoors={saltoSpaceDoors}
              saltoSpaceGroups={saltoSpaceGroups}
              saltoSpaceAccessLevels={saltoSpaceAccessLevels}
            />
          ) : (
            <>
              <div className="columns">
                <div className="description-column">
                  <div className="dropdown-buttons-container">
                    <div className="dropdown-buttons-container-row">
                      <div className="dropdown-container">
                        <div className="dropdown-header">
                          {t('clients-legacy:profileView.integrations.doorLabel')}
                        </div>
                        <Select
                          styles={saltoSpaceDropdownStyles()}
                          classNamePrefix="dropdown-button"
                          options={saltoSpaceDoors}
                          getOptionLabel={(option) => option.name.toString()}
                          getOptionValue={(option) => option.saltoId}
                          value={getSelectedItemsValue(fobSelectedDoors, saltoSpaceDoors)}
                          onChange={(selectedOptions) =>
                            setFobSelectedDoors(
                              selectedOptions ? selectedOptions.map((o) => o.saltoId) : [],
                            )
                          }
                          placeholder={t(
                            'clients-legacy:profileView.integrations.selectDoorPlaceholder',
                          )}
                          isClearable
                          isMulti
                          closeMenuOnSelect={false}
                        />
                      </div>
                      <div className="dropdown-container">
                        <div className="dropdown-header">
                          {t('clients-legacy:profileView.integrations.zoneLabel')}
                        </div>
                        <Select
                          styles={saltoSpaceDropdownStyles()}
                          className="basic-multi-select"
                          classNamePrefix="select"
                          options={saltoSpaceGroups}
                          getOptionLabel={(option) => option.name.toString()}
                          getOptionValue={(option) => option.saltoId}
                          value={getSelectedItemsValue(fobSelectedGroups, saltoSpaceGroups)}
                          onChange={(selectedOptions) =>
                            setFobSelectedGroups(
                              selectedOptions ? selectedOptions.map((o) => o.saltoId) : [],
                            )
                          }
                          placeholder={t('clients:keylessAccess.saltoSpace.zone.placeholder')}
                          isClearable
                          isMulti
                          closeMenuOnSelect={false}
                        />
                      </div>
                    </div>
                    <div className="dropdown-buttons-container-row">
                      <div className="dropdown-container">
                        <div className="dropdown-header">
                          {t('clients-legacy:profileView.integrations.accessLevelLabel')}
                        </div>
                        <Select
                          styles={saltoSpaceDropdownStyles()}
                          className="basic-multi-select"
                          classNamePrefix="select"
                          options={saltoSpaceAccessLevels}
                          getOptionLabel={(option) => option.name.toString()}
                          getOptionValue={(option) => option.saltoId}
                          value={getSelectedItemsValue(
                            fobSelectedAccessLevels,
                            saltoSpaceAccessLevels,
                          )}
                          onChange={(selectedOptions) =>
                            setFobSelectedAccessLevels(
                              selectedOptions ? selectedOptions.map((o) => o.saltoId) : [],
                            )
                          }
                          placeholder={t(
                            'clients:keylessAccess.saltoSpace.accessLevel.placeholder',
                          )}
                          isClearable
                          isMulti
                          closeMenuOnSelect={false}
                        />
                      </div>
                      <div className="dropdown-container">
                        <div className="dropdown-header">
                          {t('clients-legacy:profileView.integrations.expirationDateLabel')}
                        </div>
                        <DatePicker
                          className="dropdown-button"
                          placeholderText={t(
                            'clients-legacy:profileView.integrations.dateFormatPlaceholder',
                          )}
                          selected={fobExpirationDate || null}
                          onChange={(selectedDate) =>
                            setFobExpirationDate(new Date(selectedDate).getTime() || null)
                          }
                          minDate={getCurrentDate()}
                          isClearable
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="confirm-button button-primary button"
                disabled={!isFobButtonEnabled}
                onClick={() => setIsFobModalOpen(true)}
              >
                {t('clients-legacy:profileView.integrations.saltoSpace.issueFob')}
              </button>
            </>
          )}
          <SaltoSpaceIssueFobModal
            isOpen={isFobModalOpen}
            buildingId={buildingId}
            allowOverride
            onProgram={handleIssueFob}
            onClose={() => setIsFobModalOpen(false)}
            onSuccess={handleFobSuccess}
          />
        </>
      )}
      <SaltoSpaceExternalFobs
        isAvailable={isFobsAvailable && isUserConnectedToSaltoSpace}
        buildingId={buildingId}
        email={email}
        saltoSpaceDoors={saltoSpaceDoors}
        saltoSpaceGroups={saltoSpaceGroups}
        saltoSpaceAccessLevels={saltoSpaceAccessLevels}
      />
      <ConfirmPopup
        title={t('common:confirmPopup.caution')}
        body={t('clients:keylessAccess.saltoSpace.createUser.confirmBody')}
        isOpen={isCreateNewUserPopupVisible}
        cancelText={t('common:actions.close')}
        confirmText={t('clients-legacy:profileView.integrations.yesCreate')}
        cancelCallback={() => setIsCreateNewUserPopupVisible(false)}
        confirmCallback={handleCreateNewUser}
        className="is-caution"
        isLoading={isCreatingNewUser}
      />
    </div>
  )
}

export default SaltoSpaceSection
